/**
 * queue-refusal: what a visitor reads when a queue join is refused because the queue is closed or
 * full (CC 0343, 0356). Both refusals carry the registry's canonical message, which is for diagnosis;
 * the reopening sits in `error.params` (next_open_at: an instant, next_open_time: the venue's own
 * label, timezone), each possibly null. Every join surface (the queue page, the walk-in journey, the
 * One-Stop join, the journey page) words it the same way from the code and the params.
 *
 * Returns null for any other refusal, so the caller keeps its own handling.
 */
type Dict = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

function dayKey(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

/** "It opens at 9:00 AM CDT." / "It opens Tuesday at 9:00 AM CDT." in the queue's zone; "" if unreadable. */
function opensSentence(atIso: string, timeZone: string, now: Date): string {
  const at = new Date(atIso);
  if (Number.isNaN(at.getTime())) return "";
  let zone = timeZone;
  try { new Intl.DateTimeFormat("en-US", { timeZone: zone }).format(at); } catch { zone = ""; }
  const opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", timeZoneName: "short", ...(zone ? { timeZone: zone } : {}) };
  const time = new Intl.DateTimeFormat(undefined, opts).format(at);
  const today = zone ? dayKey(at, zone) === dayKey(now, zone) : at.toDateString() === now.toDateString();
  if (today) return "It opens at " + time + ".";
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long", ...(zone ? { timeZone: zone } : {}) }).format(at);
  return "It opens " + weekday + " at " + time + ".";
}

export function queueRefusalText(err: unknown, now: Date = new Date()): string | null {
  const e = (err && typeof err === "object" ? err : {}) as Dict;
  const code = str(e.code);
  if (code === "QUEUE_CAPACITY_LIMIT_EXCEEDED") return "This queue is full right now. Please try again in a few minutes.";
  if (code !== "QUEUE_HOURS_UNAVAILABLE") return null;
  const params = (e.params && typeof e.params === "object" ? e.params : {}) as Dict;
  const closed = "This queue is closed right now.";
  const at = str(params.next_open_at);
  if (at) {
    const opens = opensSentence(at, str(params.timezone), now);
    if (opens) return closed + " " + opens;
  }
  const label = str(params.next_open_time);
  if (label) return closed + " It opens at " + label + ".";
  return closed;
}
